import React, { useEffect, useState } from 'react';
import { Save, Lock, Unlock, RefreshCw, Settings2, Plus, Trash2, UserPlus, X } from 'lucide-react';
export interface CustomShift {
  id: string;
  code: string;
  name: string;
  timing: string;
  color: string;
}
const DEFAULT_SHIFTS: CustomShift[] = [{
  id: 's1',
  code: 'M',
  name: 'Morning',
  timing: '06:00 - 14:00',
  color: 'green'
}, {
  id: 's2',
  code: 'E',
  name: 'Evening',
  timing: '14:00 - 22:00',
  color: 'blue'
}, {
  id: 's3',
  code: 'N',
  name: 'Night',
  timing: '22:00 - 06:00',
  color: 'purple'
}, {
  id: 's4',
  code: 'OFF',
  name: 'OFF',
  timing: '-',
  color: 'gray'
}];
const COLOR_CLASSES: Record<string, string> = {
  green: 'bg-green-100 text-green-700 border-green-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  gray: 'bg-gray-100 text-gray-600 border-gray-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200'
};
const LOCATIONS = ['Entry Gate', 'Warehouse', 'Billing Counter', 'Customs Area', 'Container Yard', 'Admin Block', 'Parking Zone', 'Main Terminal'];
const EMPLOYEES = ['Rishi', 'Sanjay', 'Priya', 'Amit', 'Neha', 'Rajesh', 'Sunita', 'Manoj', 'Kavita', 'Deepak', 'Anita', 'Vikram'];
const generateDates = (days: number) => {
  const dates = [];
  const start = new Date();
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dayName = d.toLocaleDateString('en-US', {
      weekday: 'short'
    });
    const dayNum = d.getDate();
    dates.push(`${dayName} ${dayNum}`);
  }
  return dates;
};
const WEEK_DATES = generateDates(7);
const MONTH_DATES = generateDates(30);
interface Assignment {
  id: string;
  employee: string;
  shift: string;
}
// Each cell now holds an ARRAY of assignments
type RosterState = Record<string, Record<string, Assignment[]>>;
export function Roster() {
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [dates, setDates] = useState(WEEK_DATES);
  const [roster, setRoster] = useState<RosterState>({});
  const [shifts, setShifts] = useState<CustomShift[]>(DEFAULT_SHIFTS);
  const [isLocked, setIsLocked] = useState(false);
  const [showManageShifts, setShowManageShifts] = useState(false);
  // Assignment Modal State
  const [selectedCell, setSelectedCell] = useState<{
    location: string;
    date: string;
  } | null>(null);
  const [assignEmployee, setAssignEmployee] = useState('');
  const [assignShift, setAssignShift] = useState('M');
  // New Shift Form State
  const [newShiftCode, setNewShiftCode] = useState('');
  const [newShiftName, setNewShiftName] = useState('');
  const [newShiftTiming, setNewShiftTiming] = useState('');
  const [newShiftColor, setNewShiftColor] = useState('green');
  // Initialize empty roster
  useEffect(() => {
    const initial: RosterState = {};
    LOCATIONS.forEach(loc => {
      initial[loc] = {};
      MONTH_DATES.forEach(date => {
        initial[loc][date] = [];
      });
    });
    setRoster(initial);
  }, []);
  const handleViewModeChange = (mode: 'week' | 'month') => {
    setViewMode(mode);
    setDates(mode === 'week' ? WEEK_DATES : MONTH_DATES);
  };
  const handleAutoGenerate = () => {
    if (isLocked) return;
    const newRoster: RosterState = {};
    const activeShifts = shifts.filter(s => s.code !== 'OFF').map(s => s.code);
    LOCATIONS.forEach(loc => {
      newRoster[loc] = {};
      dates.forEach(date => {
        const assignments: Assignment[] = [];
        // Assign 1-3 employees per location per date
        const count = Math.floor(Math.random() * 3) + 1;
        const usedEmps = new Set<string>();
        for (let i = 0; i < count; i++) {
          let emp = EMPLOYEES[Math.floor(Math.random() * EMPLOYEES.length)];
          let attempts = 0;
          while (usedEmps.has(emp) && attempts < 10) {
            emp = EMPLOYEES[Math.floor(Math.random() * EMPLOYEES.length)];
            attempts++;
          }
          if (!usedEmps.has(emp)) {
            usedEmps.add(emp);
            const shift = activeShifts[Math.floor(Math.random() * activeShifts.length)];
            assignments.push({
              id: Math.random().toString(36).substr(2, 9),
              employee: emp,
              shift
            });
          }
        }
        newRoster[loc][date] = assignments;
      });
    });
    setRoster(prev => ({
      ...prev,
      ...newRoster
    }));
  };
  const handleCellClick = (location: string, date: string) => {
    if (isLocked) return;
    setAssignEmployee('');
    setAssignShift(shifts[0]?.code || 'M');
    setSelectedCell({
      location,
      date
    });
  };
  const handleAddAssignment = () => {
    if (!selectedCell || !assignEmployee) return;
    const newAssignment: Assignment = {
      id: Math.random().toString(36).substr(2, 9),
      employee: assignEmployee,
      shift: assignShift
    };
    setRoster(prev => ({
      ...prev,
      [selectedCell.location]: {
        ...prev[selectedCell.location],
        [selectedCell.date]: [...(prev[selectedCell.location]?.[selectedCell.date] || []), newAssignment]
      }
    }));
    // Reset form for next entry
    setAssignEmployee('');
    setAssignShift(shifts[0]?.code || 'M');
  };
  const handleRemoveAssignment = (location: string, date: string, assignmentId: string) => {
    setRoster(prev => ({
      ...prev,
      [location]: {
        ...prev[location],
        [date]: (prev[location]?.[date] || []).filter(a => a.id !== assignmentId)
      }
    }));
  };
  const handleClearAllAssignments = () => {
    if (!selectedCell) return;
    setRoster(prev => ({
      ...prev,
      [selectedCell.location]: {
        ...prev[selectedCell.location],
        [selectedCell.date]: []
      }
    }));
  };
  const handleAddShift = () => {
    if (!newShiftCode || !newShiftName) return;
    setShifts([...shifts, {
      id: Math.random().toString(36).substr(2, 9),
      code: newShiftCode.toUpperCase(),
      name: newShiftName,
      timing: newShiftTiming || '-',
      color: newShiftColor
    }]);
    setNewShiftCode('');
    setNewShiftName('');
    setNewShiftTiming('');
  };
  const handleDeleteShift = (id: string) => {
    setShifts(shifts.filter(s => s.id !== id));
  };
  const getShiftColorClass = (code: string) => {
    const shift = shifts.find(s => s.code === code);
    if (!shift) return 'bg-gray-100 text-gray-600 border-gray-200';
    return COLOR_CLASSES[shift.color] || COLOR_CLASSES['gray'];
  };
  const getShiftInfo = (code: string) => {
    return shifts.find(s => s.code === code);
  };
  // Get employees already assigned in the current cell (for the modal)
  const currentCellAssignments = selectedCell ? roster[selectedCell.location]?.[selectedCell.date] || [] : [];
  return <div className="p-6 max-w-[1600px] mx-auto h-[calc(100vh-64px)] flex flex-col relative" data-id="element-1487">
      {/* Top Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-1488">
        <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200 shadow-sm" data-id="element-1489">
          <button onClick={() => handleViewModeChange('week')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'week' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1490">
            Week
          </button>
          <button onClick={() => handleViewModeChange('month')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'month' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1491">
            Month
          </button>
        </div>

        <div className="flex items-center gap-3" data-id="element-1492">
          <button onClick={() => setShowManageShifts(!showManageShifts)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${showManageShifts ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1493">
            <Settings2 size={16} data-id="element-1494" />
            Manage Shifts
          </button>
          <button onClick={handleAutoGenerate} disabled={isLocked} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1495">
            <RefreshCw size={16} data-id="element-1496" />
            Auto Generate
          </button>
          <button onClick={() => setIsLocked(!isLocked)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${isLocked ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1497">
            {isLocked ? <Lock size={16} data-id="element-1498" /> : <Unlock size={16} data-id="element-1499" />}
            {isLocked ? 'Locked' : 'Lock Roster'}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-1500">
            <Save size={16} data-id="element-1501" />
            Save Roster
          </button>
        </div>
      </div>

      {/* Manage Shifts Panel */}
      {showManageShifts && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 animate-in slide-in-from-top-4 duration-300 flex-shrink-0" data-id="element-1502">
          <h3 className="text-lg font-bold text-gray-800 mb-4" data-id="element-1503">
            Custom Duty Shifts
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1504">
            <div data-id="element-1505">
              <table className="w-full text-sm text-left border border-gray-200 rounded-lg overflow-hidden" data-id="element-1506">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase border-b border-gray-200" data-id="element-1507">
                  <tr data-id="element-1508">
                    <th className="px-4 py-2" data-id="element-1509">Code</th>
                    <th className="px-4 py-2" data-id="element-1510">Name</th>
                    <th className="px-4 py-2" data-id="element-1511">Timing</th>
                    <th className="px-4 py-2 text-center" data-id="element-1512">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-1513">
                  {shifts.map(shift => <tr key={shift.id} className="hover:bg-gray-50" data-id="element-1514">
                      <td className="px-4 py-2" data-id="element-1515">
                        <span className={`px-2 py-1 rounded text-xs font-bold border ${COLOR_CLASSES[shift.color]}`} data-id="element-1516">
                          {shift.code}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-medium text-gray-800" data-id="element-1517">
                        {shift.name}
                      </td>
                      <td className="px-4 py-2 text-gray-600" data-id="element-1518">
                        {shift.timing}
                      </td>
                      <td className="px-4 py-2 text-center" data-id="element-1519">
                        <button onClick={() => handleDeleteShift(shift.id)} className="text-gray-400 hover:text-red-500 p-1 transition-colors" disabled={shift.code === 'OFF'} data-id="element-1520">
                          <Trash2 size={16} data-id="element-1521" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1522">
              <h4 className="text-sm font-bold text-gray-700 mb-3" data-id="element-1523">
                Add New Shift
              </h4>
              <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-1524">
                <div data-id="element-1525">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1526">
                    Code
                  </label>
                  <input type="text" value={newShiftCode} onChange={e => setNewShiftCode(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 outline-none" maxLength={4} data-id="element-1527" />
                </div>
                <div data-id="element-1528">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1529">
                    Name
                  </label>
                  <input type="text" value={newShiftName} onChange={e => setNewShiftName(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-1530" />
                </div>
                <div data-id="element-1531">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1532">
                    Timing
                  </label>
                  <input type="text" value={newShiftTiming} onChange={e => setNewShiftTiming(e.target.value)} placeholder="00:00 - 00:00" className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-1533" />
                </div>
                <div data-id="element-1534">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1535">
                    Color
                  </label>
                  <select value={newShiftColor} onChange={e => setNewShiftColor(e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white" data-id="element-1536">
                    {Object.keys(COLOR_CLASSES).map(color => <option key={color} value={color} data-id="element-1537">
                        {color.charAt(0).toUpperCase() + color.slice(1)}
                      </option>)}
                  </select>
                </div>
              </div>
              <button onClick={handleAddShift} disabled={!newShiftCode || !newShiftName} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded text-sm font-medium transition-colors disabled:opacity-50" data-id="element-1538">
                <Plus size={16} data-id="element-1539" /> Add Shift
              </button>
            </div>
          </div>
        </div>}

      {/* Main Grid */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-0" data-id="element-1540">
        <div className="overflow-auto flex-1 scrollbar-custom" data-id="element-1541">
          <table className="w-full text-sm text-left border-collapse" data-id="element-1542">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm" data-id="element-1543">
              <tr data-id="element-1544">
                <th className="px-4 py-4 font-bold text-gray-700 border-b border-r border-gray-200 bg-gray-100 sticky left-0 z-30 w-56 uppercase tracking-wider text-xs" data-id="element-1545">
                  📍 Duty Location
                </th>
                {dates.map(date => {
                const parts = date.split(' ');
                const dayName = parts[0];
                const dayNum = parts[1];
                const isWeekend = dayName === 'Sat' || dayName === 'Sun';
                return <th key={date} className={`px-3 py-3 font-semibold border-b border-r border-gray-200 text-center min-w-[160px] ${isWeekend ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-600'}`} data-id="element-1546">
                      <div className="text-xs uppercase tracking-wider" data-id="element-1547">
                        {dayName}
                      </div>
                      <div className="text-lg font-bold leading-tight" data-id="element-1548">
                        {dayNum}
                      </div>
                    </th>;
              })}
              </tr>
            </thead>
            <tbody data-id="element-1549">
              {LOCATIONS.map(location => <tr key={location} className="border-b border-gray-200 hover:bg-gray-50/30" data-id="element-1550">
                  <td className="px-4 py-4 border-r border-gray-200 sticky left-0 z-10 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]" data-id="element-1551">
                    <div className="flex items-center gap-2" data-id="element-1552">
                      <span className="text-lg" data-id="element-1553">📍</span>
                      <span className="font-bold text-gray-800 text-sm" data-id="element-1554">
                        {location}
                      </span>
                    </div>
                  </td>
                  {dates.map(date => {
                const assignments = roster[location]?.[date] || [];
                return <td key={`${location}-${date}`} className="p-1.5 border-r border-gray-200 align-top cursor-pointer group" onClick={() => handleCellClick(location, date)} data-id="element-1555">
                        <div className={`w-full min-h-[80px] rounded-lg border p-1.5 transition-all ${assignments.length > 0 ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-dashed border-gray-300 hover:bg-indigo-50 hover:border-indigo-300'} ${isLocked ? 'cursor-default' : 'hover:scale-[1.01]'}`} data-id="element-1556">
                          {assignments.length > 0 ? <div className="space-y-1" data-id="element-1557">
                              {assignments.map(a => {
                        const shiftInfo = getShiftInfo(a.shift);
                        return <div key={a.id} className={`rounded-md border px-2 py-1.5 ${getShiftColorClass(a.shift)}`} data-id="element-1558">
                                    <div className="font-bold text-xs leading-tight" data-id="element-1559">
                                      {a.employee}
                                    </div>
                                    <div className="flex items-center gap-1 mt-0.5" data-id="element-1560">
                                      <span className="text-[10px] font-bold" data-id="element-1561">
                                        {shiftInfo?.name || a.shift}
                                      </span>
                                      {shiftInfo?.timing && shiftInfo.timing !== '-' && <span className="text-[9px] opacity-70" data-id="element-1562">
                                            • {shiftInfo.timing}
                                          </span>}
                                    </div>
                                  </div>;
                      })}
                              {!isLocked && <div className="flex items-center justify-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1563">
                                  <Plus size={12} className="text-gray-400" data-id="element-1564" />
                                </div>}
                            </div> : <div className="flex flex-col items-center justify-center h-full min-h-[72px] gap-1 text-gray-400" data-id="element-1565">
                              <UserPlus size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1566" />
                              <span className="text-[10px] opacity-0 group-hover:opacity-60 transition-opacity" data-id="element-1567">
                                Assign
                              </span>
                            </div>}
                        </div>
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assignment Modal — now supports multiple */}
      {selectedCell && <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedCell(null)} data-id="element-1568">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-1569">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50" data-id="element-1570">
              <div data-id="element-1571">
                <h3 className="font-bold text-gray-900" data-id="element-1572">Manage Assignments</h3>
                <p className="text-xs text-gray-500" data-id="element-1573">
                  📍 {selectedCell.location} • {selectedCell.date}
                </p>
              </div>
              <button onClick={() => setSelectedCell(null)} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-1574">
                <X size={20} data-id="element-1575" />
              </button>
            </div>

            <div className="p-5 space-y-4" data-id="element-1576">
              {/* Current Assignments */}
              {currentCellAssignments.length > 0 && <div data-id="element-1577">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2" data-id="element-1578">
                    Current Assignments ({currentCellAssignments.length})
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto" data-id="element-1579">
                    {currentCellAssignments.map(a => {
                const shiftInfo = getShiftInfo(a.shift);
                return <div key={a.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${getShiftColorClass(a.shift)}`} data-id="element-1580">
                          <div data-id="element-1581">
                            <span className="font-bold text-sm" data-id="element-1582">
                              {a.employee}
                            </span>
                            <span className="text-xs ml-2 opacity-80" data-id="element-1583">
                              {shiftInfo?.name || a.shift}{' '}
                              {shiftInfo?.timing && shiftInfo.timing !== '-' ? `(${shiftInfo.timing})` : ''}
                            </span>
                          </div>
                          {!isLocked && <button onClick={() => handleRemoveAssignment(selectedCell.location, selectedCell.date, a.id)} className="p-1 hover:bg-white/50 rounded transition-colors" title="Remove" data-id="element-1584">
                              <X size={14} data-id="element-1585" />
                            </button>}
                        </div>;
              })}
                  </div>
                </div>}

              {/* Add New Assignment */}
              {!isLocked && <div className="border-t border-gray-100 pt-4" data-id="element-1586">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-2" data-id="element-1587">
                    Add New Assignment
                  </label>
                  <div className="space-y-3" data-id="element-1588">
                    <select value={assignEmployee} onChange={e => setAssignEmployee(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-medium text-gray-800" data-id="element-1589">
                      <option value="" data-id="element-1590">-- Select Employee --</option>
                      {EMPLOYEES.map(emp => <option key={emp} value={emp} data-id="element-1591">
                          {emp}
                        </option>)}
                    </select>

                    {assignEmployee && <div className="grid grid-cols-4 gap-2" data-id="element-1592">
                        {shifts.map(shift => <button key={shift.code} onClick={() => setAssignShift(shift.code)} className={`py-2 rounded-md font-bold text-xs border transition-all ${assignShift === shift.code ? `${COLOR_CLASSES[shift.color]} ring-2 ring-offset-1` : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1593">
                            {shift.code}
                          </button>)}
                      </div>}

                    <button onClick={handleAddAssignment} disabled={!assignEmployee} className="w-full flex items-center justify-center gap-2 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1594">
                      <Plus size={16} data-id="element-1595" />
                      Add to This Cell
                    </button>
                  </div>
                </div>}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex gap-3" data-id="element-1596">
              {currentCellAssignments.length > 0 && !isLocked && <button onClick={handleClearAllAssignments} className="flex-1 py-2 bg-white border border-red-200 text-red-600 rounded-lg text-sm font-bold hover:bg-red-50 transition-colors" data-id="element-1597">
                  Clear All
                </button>}
              <button onClick={() => setSelectedCell(null)} className="flex-1 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors" data-id="element-1598">
                Done
              </button>
            </div>
          </div>
        </div>}
    </div>;
}